import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import PaymentIframe, { PelecardResponse } from '../../i-fram-asd';
import {
  generateCreditCardTokenLinkOperation,
  GenerateCreditCardTokenLinkResponse,
} from '../operations/generate-credit-card-token-link-operation';
import { toast } from 'react-toastify';
import appApi from '../../../../../api/app-api';
import globalUserPage from '../../../../../stores/global-user-page';

export interface AddNewCardDialogProps {
  visible: boolean;
  onHide: () => void;
  userId: number;
  onSuccess?: () => void;
}

export const AddNewCardDialog: React.FC<AddNewCardDialogProps> = ({
  visible,
  onHide,
  userId,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [tokenLinkData, setTokenLinkData] =
    useState<GenerateCreditCardTokenLinkResponse | null>(null);
  const [showIframe, setShowIframe] = useState(false);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (visible) {
      setTokenLinkData(null);
      setShowIframe(false);
    }
  }, [visible]);

  const handleGenerateLink = async () => {
    const result = await generateCreditCardTokenLinkOperation(
      userId,
      setLoading,
    );
    if (result) {
      setTokenLinkData(result);
      setShowIframe(true);
    }
  };

  const handlePaymentSuccess = async (data: PelecardResponse) => {
    if (!tokenLinkData) {
      toast.error('שגיאה בהוספת כרטיס האשראי - חסרים פרטי עסקה');
      return;
    }

    setLoading(true);

    try {
      // Verify the token with the backend
      await appApi.verifyCreditCardToken(
        userId,
        tokenLinkData.transactionId,
        tokenLinkData.confirmationId,
      );

      toast.success('כרטיס האשראי נוסף בהצלחה');

      // Refresh user details to get the new credit card token
      const updatedUserDetails = await appApi.getUserStudentDetails(userId);
      globalUserPage.studentDetails = updatedUserDetails.data.body;

      onSuccess?.();
      onHide();
    } catch (e) {
      console.error('Error verifying credit card token:', e);
      toast.error('שגיאה באימות כרטיס האשראי');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentError = async (data?: PelecardResponse) => {
    if (tokenLinkData) {
      try {
        // Notify backend about the failure
        await appApi.verifyCreditCardToken(
          userId,
          tokenLinkData.transactionId,
          tokenLinkData.confirmationId,
        );
      } catch (e) {
        console.error('Error notifying backend about payment failure:', e);
      }
    }

    toast.error('שגיאה בהוספת כרטיס האשראי');
    setShowIframe(false);
    setTokenLinkData(null);
  };

  // Use clearance feedback page for success/fail detection
  // The feedback page will send postMessage to notify this component
  const currentOrigin = window.location.origin;
  const successLink = `${currentOrigin}/clearance/feedback-page/success`;
  const failLink = `${currentOrigin}/clearance/feedback-page/failure`;

  return (
    <Dialog
      header="הוספת כרטיס אשראי חדש"
      visible={visible}
      onHide={onHide}
      style={{
        width: showIframe ? '700px' : '500px',
        height: showIframe ? '600px' : 'auto',
      }}
      contentStyle={{ height: showIframe ? '500px' : 'auto' }}
    >
      {loading ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '48px',
          }}
        >
          <ProgressSpinner
            style={{ width: '50px', height: '50px' }}
            strokeWidth="4"
          />
          <p style={{ marginTop: '16px', color: '#666' }}>
            טוען קישור להוספת כרטיס...
          </p>
        </div>
      ) : showIframe && tokenLinkData ? (
        <div style={{ height: '100%', width: '100%' }}>
          <PaymentIframe
            src={tokenLinkData.url}
            successLink={successLink}
            failLink={failLink}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentError={handlePaymentError}
          />
        </div>
      ) : (
        <div
          style={{
            padding: '24px',
            textAlign: 'center',
            color: '#666',
          }}
        >
          <i
            className="pi pi-credit-card"
            style={{
              fontSize: '48px',
              color: '#388E3C',
              marginBottom: '16px',
              display: 'block',
            }}
          ></i>
          <p style={{ fontSize: '16px', marginBottom: '8px' }}>
            הוסף כרטיס אשראי חדש לחשבונך
          </p>
          <p
            style={{ fontSize: '14px', color: '#9CA3AF', marginBottom: '24px' }}
          >
            הכרטיס ישמש לתשלומים עתידיים והוראות קבע
          </p>
          <Button
            label="התחל הוספת כרטיס"
            icon="pi pi-plus"
            onClick={handleGenerateLink}
            className="p-button-success"
            style={{ width: '100%' }}
          />
        </div>
      )}
    </Dialog>
  );
};
